import { createContext, useContext, ReactNode, useEffect } from "react";
import { useRegion, Region, formatPrice, convertPrice, CURRENCY_CONFIG } from "@/hooks/use-region";
import { useLanguage } from "@/contexts/LanguageContext";

interface RegionContextType {
  region: Region;
  loading: boolean;
  setRegion: (r: Region) => void;
  currency: (typeof CURRENCY_CONFIG)[Region];
  formatPrice: (egpAmount: number) => string;
  convertPrice: (egpAmount: number) => number;
  isGCC: boolean;
}

const RegionContext = createContext<RegionContextType | undefined>(undefined);

export const RegionProvider = ({ children }: { children: ReactNode }) => {
  const { region, loading, setRegion } = useRegion();
  const { lang, toggleLanguage, setRegionDialect } = useLanguage();

  // Sync region dialect with language context
  useEffect(() => {
    if (!loading) {
      setRegionDialect(region);
    }
  }, [region, loading, setRegionDialect]);

  // Auto-switch language based on region on first visit
  useEffect(() => {
    if (loading) return;
    const hasVisited = localStorage.getItem("has_visited");
    if (hasVisited) return;

    if (region === "other" && lang === "ar") {
      toggleLanguage();
    }
    localStorage.setItem("has_visited", "true");
  }, [region, loading]);

  const value: RegionContextType = {
    region,
    loading,
    setRegion: (r: Region) => {
      setRegion(r);
      setRegionDialect(r);
    },
    currency: CURRENCY_CONFIG[region],
    formatPrice: (egpAmount: number) => formatPrice(egpAmount, region, lang),
    convertPrice: (egpAmount: number) => convertPrice(egpAmount, region),
    isGCC: region === "sa",
  };

  return <RegionContext.Provider value={value}>{children}</RegionContext.Provider>;
};

export const useRegionContext = () => {
  const ctx = useContext(RegionContext);
  if (!ctx) throw new Error("useRegionContext must be used within RegionProvider");
  return ctx;
};
